import type { ColumnDef } from '@tanstack/react-table';

import { toAge } from '@virtual-care/isomorphic';
import { Badge } from '@virtual-care/shadcn';

export function phoneNumberColumn<
  T extends { phoneNumber: string },
>(): ColumnDef<T> {
  return {
    accessorKey: 'phoneNumber',
    header: 'Phone Number',
    cell: ({ row }) => (
      <div className="font-mono">{row.getValue('phoneNumber') || 'N/A'}</div>
    ),
  };
}

export function emailColumn<T extends { email: string }>(): ColumnDef<T> {
  return {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => <div className="lowercase">{row.getValue('email')}</div>,
  };
}

export function genderColumn<T extends { gender?: string }>(): ColumnDef<T>;
export function genderColumn<T>(
  accessor: (row: T) => { gender?: string } | undefined,
): ColumnDef<T>;
export function genderColumn<T extends { gender?: string }>(
  accessor?: (row: T) => { gender?: string } | undefined,
): ColumnDef<T> {
  return {
    accessorKey: 'gender',
    header: 'Gender',
    cell: ({ row }) => {
      const original = accessor ? accessor(row.original) : row.original;
      const gender = original?.gender;
      const label =
        gender === 'male' ? 'Male' : gender === 'female' ? 'Female' : null;
      return (
        <div>
          {label ? (
            <Badge variant="outline">{label}</Badge>
          ) : (
            <span className="text-muted-foreground">N/A</span>
          )}
        </div>
      );
    },
  };
}

export function ageColumn<T extends { birthDate?: string }>(): ColumnDef<T>;
export function ageColumn<T>(
  accessor: (row: T) => { birthDate?: string } | undefined,
): ColumnDef<T>;
export function ageColumn<T extends { birthDate?: string }>(
  accessor?: (row: T) => { birthDate?: string } | undefined,
): ColumnDef<T> {
  return {
    accessorKey: 'birthDate',
    header: 'Age',
    cell: ({ row }) => {
      const original = accessor ? accessor(row.original) : row.original;
      return original?.birthDate ? (
        <div>{toAge(original)} years</div>
      ) : (
        <span className="text-muted-foreground">N/A</span>
      );
    },
  };
}
